import { useCanManageEstate } from '../../services/access-policy';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Tag } from 'antd';
import { FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import { probateDocsService, ProbateDocDetail } from './probatedocs.service';
import PdfPreviewModal from './PdfPreviewModal';
import DetailPage, {
  DetailFields,
  DetailSection,
  DetailStats,
  Field,
} from '../../components/DetailPage';

/** The stored path is a server location; only its file name is useful here. */
const fileNameOf = (path?: string | null) => (path ? path.split('/').pop() : null);

const ProbateDocDetails: React.FC = () => {
  const { probateDocId } = useParams<{ probateDocId: string }>();
  const navigate = useNavigate();
  const canManage = useCanManageEstate();
  const [doc, setDoc] = useState<ProbateDocDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!probateDocId) {
        setError('No document was specified.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setDoc(await probateDocsService.getProbateDoc(probateDocId));
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [probateDocId]);

  const tags = [
    doc?.status ? { label: doc.status, color: doc.status === 'Updated' ? 'green' : undefined } : null,
    doc?.templateName ? { label: doc.templateName, color: 'blue' } : null,
  ].filter(Boolean) as { label: string; color?: string }[];

  return (
    <DetailPage
      eyebrow="Probate document"
      title={doc?.title || 'Probate document'}
      icon={<FileTextOutlined />}
      tags={tags}
      loading={loading}
      error={error}
      backTo="/probate-docs"
      backLabel="Back to probate docs"
      actions={
        doc && (
          <>
            <Button disabled={!canManage} onClick={() => navigate(`/probate-docs/${doc.uid}/map-fields`)}>Map fields</Button>
            <Button disabled={!canManage} type="primary" onClick={() => navigate(`/probate-docs/${doc.uid}/visual-mapper`)}>
              Visual mapper
            </Button>
            <Button icon={<EyeOutlined />} onClick={() => setPreviewVisible(true)}>
              Preview Filled
            </Button>
          </>
        )
      }
    >
      <DetailStats
        stats={[
          { label: 'Status', value: doc?.status },
          { label: 'Versions', value: doc ? `${doc.versionCount}` : null },
          { label: 'Template', value: doc?.templateName, emptyText: 'None' },
          { label: 'Estate', value: doc?.estateName, emptyText: 'Not linked' },
        ]}
      />

      <DetailSection title="Overview">
        <DetailFields>
          <Field label="Description" value={doc?.description} span />
          <Field label="File" value={fileNameOf(doc?.filePath)} />
          <Field
            label="Annotated file"
            value={fileNameOf(doc?.annotatedFilePath)}
            emptyText="Not generated yet"
          />
          <Field label="Estate" value={doc?.estateName} emptyText="Not linked to an estate" />
          <Field label="Template" value={doc?.templateName} emptyText="No template" />
        </DetailFields>
      </DetailSection>

      <DetailSection
        title={`Tags (${doc?.tags?.length || 0})`}
        empty={!doc?.tags?.length}
        emptyText="No tags on this document"
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {doc?.tags?.map((tag) => (
            <Tag key={tag.id}>{tag.name}</Tag>
          ))}
        </div>
      </DetailSection>

      {doc && (
        <PdfPreviewModal
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          probateDocId={doc.uid}
          docTitle={doc.title}
          estateId={doc.estateId || undefined}
          estateName={doc.estateName || undefined}
        />
      )}
    </DetailPage>
  );
};

export default ProbateDocDetails;
